import { useEffect } from 'react';
import { Control, FieldValues, useFormContext, UseFormRegister } from 'react-hook-form';

import ArraysInput from '../../../components/form/ArraysInput';
import Input from '../../../components/form/Input';
import RadioGroupButton from '../../../components/form/RadioGroupButton';
import { BUNDLES_CATEGORY_OPTIONS, EBOOK_HAS_DISCOUNT, EBOOK_HAS_DISCOUNT_END_DATE, IConfigItemType, SORT_BY_QUERY_EBOOK_BUNDLE_OPTIONS } from '../validates/sectionBuilderSchema';

import DropdownSelectNested from './DropdownSelectNested';
import SelectDiscountSetting from './SelectDiscountSetting';

export interface IPropsQueryEbookBundleItem {
  type: IConfigItemType.QueryEbookBundle;
  categoryId: number;
  hasDiscount?: string;
  hasDiscountEnded?: string;
  gteDiscountRatio?: number;
  lteDiscountRatio?: number;
  anyUserIds?: string[];
  whitelistIds?: string[];
  blacklistIds?: string[];
  tagNames?: string[];
  anyTagNames?: string[];
  isFree?: string;
  sortType?: 'asc' | 'desc';
  sortBy?: '' | 'discountEnded' | 'started' | 'created' | 'trending' | 'rating' | 'bestseller' | 'bestseller7Days' | 'bestseller30Days' | 'priceThb' | 'title' | 'random';
  limit?: number;
}

interface IPropsQueryEbookBundleSectionItem {
  index: number;
  itemData: IPropsQueryEbookBundleItem;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>;
  register: UseFormRegister<FieldValues>;
}

const QueryEbookBundleSectionItem = (props: IPropsQueryEbookBundleSectionItem) => {
  const { index, itemData, control, register } = props;
  const { setValue, watch } = useFormContext();
  const hasDiscountEndedFieldName = `configItems.${index}.hasDiscountEnded`;
  const hasDiscountEndedValue = watch(hasDiscountEndedFieldName);

  useEffect(() => {
    if (itemData.hasDiscount && typeof hasDiscountEndedValue === 'undefined') {
      setValue(hasDiscountEndedFieldName, 'null');
    }
  }, [hasDiscountEndedFieldName, hasDiscountEndedValue, itemData.hasDiscount, setValue]);

  // ตย. input
  //   {
  //     "platform": "pinto",
  //     "note": "test note",
  //     "layoutId": "book_banner",
  //     "title": "test title",
  //     "target": null,
  //     "configItems": [
  //         {
  //             "type": "query-ebook-bundle",
  //             "hasDiscount": true,
  //             "gteDiscountRatio": "0.1",
  //             "lteDiscountRatio": "0.5",
  //             "hasDiscountEnded": true
  //         }
  //     ]
  // }

  return (
    <div className="relative flex justify-between space-x-16">
      <div className="w-full collapse">
        <input type="checkbox" className="peer" />
        <div className=" collapse-title bg-systemGrays08FillQuaternary text-systemGrays01LabelPrimary peer-checked:bg-systemGrays08FillQuaternary peer-checked:text-systemGrays01LabelPrimary flex justify-between after:content-['+'] peer-checked:after:content-['-'] ease-out duration-300">
          <span>
            <span>Query Ebook Bundle</span>
          </span>
        </div>
        <div className="collapse-content text-systemGrays01LabelPrimary bg-systemGrays08FillQuaternary !peer-checked:bg-systemGrays08FillQuaternary peer-checked:text-systemGrays01LabelPrimary">
          <div className="space-y-8">
            <RadioGroupButton label="Category" options={BUNDLES_CATEGORY_OPTIONS} register={register} name={`configItems.${index}.categoryId`} />

            <DropdownSelectNested
              label="SortBy"
              options={SORT_BY_QUERY_EBOOK_BUNDLE_OPTIONS}
              prefixName={`configItems.${index}`}
              name={[`configItems.${index}.sortBy`, `configItems.${index}.sortType`]}
            />

            <Input
              type="number"
              register={() => register(`configItems.${index}.limit`)}
              label="limit"
              name="queryEbookSectionItemLimit"
              helperText="Input number of item to show, limit at 100 items"
              placeholder="limit"
              min="0"
              max="100"
            />

            <SelectDiscountSetting options={EBOOK_HAS_DISCOUNT} prefixName={`configItems.${index}`} name={[`configItems.${index}.hasDiscount`]} label={'HasDiscount'} />

            {itemData.hasDiscount && (
              <div className="flex items-center justify-between gap-x-8">
                <Input
                  type="number"
                  register={() => register(`configItems.${index}.gteDiscountRatio`)}
                  label="min_DiscountId"
                  name="minDiscountId"
                  helperText="limited 0-50"
                  placeholder="min_DiscountId"
                  className="flex-1"
                  min="0"
                  max="50"
                />
                <span>-</span>
                <Input
                  type="number"
                  register={() => register(`configItems.${index}.lteDiscountRatio`)}
                  label="max_DiscountId"
                  name="maxDiscountId"
                  helperText="limited 0-50"
                  placeholder="max_DiscountId"
                  className="flex-1"
                  min="0"
                  max="50"
                />
              </div>
            )}

            {itemData.hasDiscount && (
              <RadioGroupButton options={EBOOK_HAS_DISCOUNT_END_DATE} name={`configItems.${index}.hasDiscountEnded`} register={register} label={'HasDiscountEndDate'} />
            )}

            <div>
              <ArraysInput
                control={control}
                name={`configItems.${index}.anyUserIds`}
                label="UserIds"
                helperText="Input User IDs, press enter to input item"
                placeholder="userIds"
              />
              <ArraysInput
                control={control}
                name={`configItems.${index}.whitelistIds`}
                label="whitelistIds"
                helperText="Input bundle IDs, press enter to input item"
                placeholder="whitelistIds"
              />
              <ArraysInput
                control={control}
                name={`configItems.${index}.blacklistIds`}
                label="blacklistIds"
                helperText="Input bundle IDs, press enter to input item"
                placeholder="blacklistIds"
              />
              <ArraysInput control={control} name={`configItems.${index}.anyTagNames`} label="anyTagNames" helperText="Input Tags" placeholder="anyTagNames" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryEbookBundleSectionItem;
